<script setup lang="ts">
/**
 * AppSwitch — переключатель (тумблер) дизайн-системы.
 * Стилизация только через токены. A11y: role="switch", aria-checked,
 * клавиатура Space/Enter, focus-visible. Размеры по Figma: трек 44×26, ручка 20×20.
 */
import { computed } from 'vue'

const props = withDefaults(
  defineProps<{
    /** v-model: включён ли переключатель */
    modelValue: boolean
    /** Заблокирован */
    disabled?: boolean
    /** Размер */
    size?: 'small' | 'medium'
    /** Подпись (можно также через слот по умолчанию) */
    label?: string
    /** id для связи с внешним <label> */
    id?: string
  }>(),
  {
    disabled: false,
    size: 'medium',
    label: '',
  },
)

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void
}>()

const toggle = () => {
  if (props.disabled) return
  emit('update:modelValue', !props.modelValue)
}

const switchClasses = computed(() => [
  'app-switch',
  `app-switch--${props.size}`,
  {
    'app-switch--on': props.modelValue,
    'app-switch--disabled': props.disabled,
  },
])
</script>

<template>
  <label class="app-switch-wrap" :class="{ 'app-switch-wrap--disabled': disabled }">
    <button
      :id="id"
      type="button"
      role="switch"
      :class="switchClasses"
      :aria-checked="modelValue"
      :aria-disabled="disabled || undefined"
      :disabled="disabled"
      @click="toggle"
      @keydown.enter.prevent="toggle"
      @keydown.space.prevent="toggle"
    >
      <span class="app-switch__knob" aria-hidden="true" />
    </button>
    <span v-if="label || $slots.default" class="app-switch__label">
      <slot>{{ label }}</slot>
    </span>
  </label>
</template>

<style scoped>
.app-switch-wrap {
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-sm, 8px);
  cursor: pointer;
  user-select: none;
}

.app-switch-wrap--disabled {
  cursor: not-allowed;
}

.app-switch {
  position: relative;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  padding: 0;
  border: none;
  border-radius: var(--radius-full, 9999px);
  background: var(--color-border-strong, #e4e4e7);
  cursor: inherit;
  transition: background-color 0.2s ease;
}

.app-switch--medium {
  width: 44px;
  height: 26px;
}

.app-switch--small {
  width: 36px;
  height: 22px;
}

.app-switch__knob {
  position: absolute;
  top: 3px;
  left: 3px;
  background: var(--color-surface, #ffffff);
  border-radius: 50%;
  box-shadow: var(--shadow-sm, 0 1px 2px 0 rgba(0, 0, 0, 0.05));
  transition: transform 0.2s ease;
}

.app-switch--medium .app-switch__knob {
  width: 20px;
  height: 20px;
}

.app-switch--small .app-switch__knob {
  width: 16px;
  height: 16px;
}

.app-switch--on {
  background: var(--color-primary, #4f46e5);
}

.app-switch--on.app-switch--medium .app-switch__knob {
  transform: translateX(18px);
}

.app-switch--on.app-switch--small .app-switch__knob {
  transform: translateX(14px);
}

.app-switch:focus-visible {
  outline: none;
  box-shadow: var(--shadow-focus, 0 0 0 3px rgba(79, 70, 229, 0.3));
}

.app-switch--disabled {
  opacity: 0.5;
}

.app-switch__label {
  font-size: var(--font-size-sm, 0.875rem);
  line-height: 1.4;
  color: var(--color-text-primary, #18181b);
}
</style>
